import {
  SelectedVariants,
  ServicePaymentDto,
  PaymentType,
} from '@wix/bookings-uou-types';
import type { Experiments } from '@wix/yoshi-flow-editor';
import { ExperimentsConsts } from '../../consts/experiments';
import {
  BookingLineItem,
  ParticipantChoices,
  PriceInfo,
} from '@wix/ambassador-bookings-v2-price-info/types';
import type {
  ServiceChoice,
  ServiceOption,
  ServiceOptions,
  ServiceVariant,
  ServiceVariants,
  Money,
} from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { ServiceOptionType } from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';
import { PaymentDetails, CustomOption } from '../../types/dynamicPrice';
import { PaymentDtoMapper, PriceUtils } from '@wix/bookings-uou-mappers';
import { PriceChoice } from '../../components/BookingsForm/Widget/PriceOptionDropdownContainer/PriceOptionDropdown/PriceOptionDropdown';

export const mapVariantsToLineItems = ({
  serviceId,
  resourceId,
  selectedVariants,
  experiments,
}: {
  serviceId: string;
  resourceId: string;
  selectedVariants: SelectedVariants[];
  experiments: Experiments;
}): BookingLineItem[] => {
  if (experiments.enabled(ExperimentsConsts.SingleLineItemPreviewPrice)) {
    const staffMemberId = selectedVariants
      .flatMap((variant) => variant.choices || [])
      .find((choice: ServiceChoice) => choice?.staffMemberId)?.staffMemberId;

    const serviceChoices = selectedVariants.map(
      ({ numberOfParticipants, choices }) => ({
        numberOfParticipants,
        choices: choices
          ?.filter((choice) => isCustomChoice(choice))
          .map(({ id, optionId }) => ({
            choiceId: id,
            optionId,
          })),
      }),
    );

    return [
      {
        serviceId,
        resourceId: staffMemberId || resourceId,
        serviceChoices,
      },
    ];
  }

  return selectedVariants.map(({ numberOfParticipants, choices }) => {
    const staffMemberId = choices?.find(
      (choice: ServiceChoice) => choice?.staffMemberId,
    )?.staffMemberId;
    const customChoices = choices?.filter((choice) => isCustomChoice(choice));
    return {
      serviceId,
      resourceId: staffMemberId || resourceId,
      numberOfParticipants,
      choices: customChoices,
    };
  });
};

export const mapPriceInfoToPaymentDetails = ({
  priceInfo,
  dynamicPriceOptions,
  locale,
  experiments,
}: {
  priceInfo: PriceInfo;
  dynamicPriceOptions: ServiceOptions;
  locale: string;
  experiments: Experiments;
}): PaymentDetails[] => {
  if (!isCustomTypeOption(dynamicPriceOptions)) {
    return [];
  }

  if (experiments.enabled(ExperimentsConsts.SingleLineItemPreviewPrice)) {
    const singleLineItem = priceInfo.bookingLineItems?.[0];
    const currency = priceInfo.currency!;
    return (
      singleLineItem?.serviceChoices?.map((serviceChoice) => ({
        numberOfParticipants: serviceChoice.numberOfParticipants || 1,
        choice: serviceChoice.choices?.[0]?.custom!,
        price: {
          value: String(serviceChoice.pricePerParticipant),
          currency,
          formattedValue: PriceUtils.getFormattedCurrency({
            price: serviceChoice.pricePerParticipant!,
            currency,
            locale,
          }),
        },
      })) || []
    );
  }

  return (
    priceInfo.bookingLineItems?.map((bookingLineItem: BookingLineItem) => ({
      numberOfParticipants: bookingLineItem.numberOfParticipants || 1,
      choice: bookingLineItem.choices?.[0]?.custom!,
      price: {
        value: String(bookingLineItem.pricePerParticipant),
        currency: priceInfo.currency!,
        formattedValue: PriceUtils.getFormattedCurrency({
          price: bookingLineItem.pricePerParticipant!,
          currency: priceInfo.currency!,
          locale,
        }),
      },
    })) || []
  );
};

function isCustomTypeOption(dynamicPriceOptions: ServiceOptions) {
  return (
    dynamicPriceOptions?.values?.length === 1 &&
    dynamicPriceOptions?.values?.[0].type === ServiceOptionType.CUSTOM
  );
}

function isCustomChoice(choice: ServiceChoice): boolean {
  return !!choice.custom || !!choice.duration;
}

export function isDynamicVariant(variant: SelectedVariants): boolean {
  return !!variant.choices?.find((choice) => isCustomChoice(choice));
}

export function mapDynamicParticipantsNumber(
  selectedVariants?: SelectedVariants[],
  experiments?: Experiments,
): {
  participantsChoices?: ParticipantChoices;
  totalParticipants?: number;
} {
  let participantsChoices: ParticipantChoices | undefined;
  let totalParticipants: number | undefined;

  if (selectedVariants) {
    const isCustomBased = isDynamicVariant(selectedVariants[0]);

    if (isCustomBased) {
      if (experiments?.enabled(ExperimentsConsts.SingleLineItemPreviewPrice)) {
        const serviceChoices = selectedVariants.map(
          ({ numberOfParticipants, choices }) => ({
            numberOfParticipants,
            choices: choices?.map(({ id, optionId }) => ({
              choiceId: id,
              optionId,
            })),
          }),
        );

        participantsChoices = {
          serviceChoices,
        };
      } else {
        participantsChoices = {
          serviceChoices: selectedVariants,
        };
      }
    } else {
      totalParticipants = selectedVariants?.[0]?.numberOfParticipants || 0;
    }
  }

  return {
    totalParticipants,
    participantsChoices,
  };
}

function serviceChoiceToDropdownOptionMapper({
  choice,
  id,
  price,
  dateRegionalSettingsLocale,
}: {
  choice: string;
  id?: string;
  price?: Money;
  dateRegionalSettingsLocale?: string;
}): PriceChoice {
  let priceText = '';
  if (price && dateRegionalSettingsLocale) {
    const paymentDtoMapper = new PaymentDtoMapper(dateRegionalSettingsLocale);
    const priceValue = Number(price.value!);

    const servicePaymentDto: ServicePaymentDto = {
      currency: price!.currency!,
      price: priceValue,
      isFree: priceValue === 0,
      paymentType: PaymentType.ONLINE, // arbitrary, not used
      minCharge: 0,
      priceText: '',
    };
    priceText = paymentDtoMapper.priceText(servicePaymentDto);
  }
  return {
    title: choice,
    choiceId: choice,
    id,
    ...(priceText ? { price: priceText } : {}),
  };
}

function mapSelectedVariantsToCustomOptions(
  customOptions: CustomOption[],
  selectedVariants: SelectedVariants[],
): CustomOption[] {
  const prepopulatedCustomOptions: CustomOption[] = customOptions;
  selectedVariants.forEach((variant) => {
    if (isDynamicVariant(variant)) {
      const optionId = variant.choices?.[0]?.optionId!;
      const selectedVariant = customOptions.find(
        (option) => option.optionId === optionId,
      );
      const custom: string = variant.choices?.[0]?.custom!;
      const userChoice = selectedVariant?.options.find(
        (choice) => choice.choiceId === custom,
      );
      if (!userChoice) {
        return;
      }
      userChoice!.numberOfParticipantsInitialValue =
        variant.numberOfParticipants;
    }
  });
  return prepopulatedCustomOptions;
}

function customServiceOptionsToCustomOptions({
  serviceOptions,
  serviceVariants,
  dateRegionalSettingsLocale,
}: {
  serviceOptions: ServiceOptions;
  serviceVariants?: ServiceVariants;
  dateRegionalSettingsLocale: string;
}): CustomOption[] {
  const customOptions: CustomOption[] = [];
  serviceOptions?.values
    ?.filter(
      (serviceOption: ServiceOption) =>
        serviceOption.type === ServiceOptionType.CUSTOM,
    )
    .forEach((serviceOption: ServiceOption) => {
      const filteredVariants = serviceVariants?.values?.filter(
        (variant: ServiceVariant) =>
          variant.choices?.[0].optionId === serviceOption.id &&
          !!variant.choices?.[0].custom,
      );
      customOptions.push({
        optionId: serviceOption.id!,
        name: serviceOption.customData?.name!,
        options:
          filteredVariants?.map((variant) =>
            serviceChoiceToDropdownOptionMapper({
              choice: variant.choices?.[0].custom!,
              id: variant.choices?.[0].id || undefined,
              price: variant.price!,
              dateRegionalSettingsLocale,
            }),
          ) || [],
      });
    });
  return customOptions;
}

export function selectedVariantsAndServiceOptionsToCustomOptions({
  selectedVariants = [],
  serviceOptions = { values: [] },
  serviceVariants = { values: [] },
  dateRegionalSettingsLocale = 'en-US',
}: {
  selectedVariants?: SelectedVariants[];
  serviceOptions?: ServiceOptions;
  serviceVariants?: ServiceVariants;
  dateRegionalSettingsLocale?: string;
}): CustomOption[] {
  let customOptions = customServiceOptionsToCustomOptions({
    serviceOptions,
    serviceVariants,
    dateRegionalSettingsLocale,
  });

  if (selectedVariants.length) {
    customOptions = mapSelectedVariantsToCustomOptions(
      customOptions,
      selectedVariants,
    );
  }

  return customOptions;
}

export function isPreferenceOption(dynamicPriceOptions: ServiceOptions) {
  return (
    dynamicPriceOptions?.values?.length === 1 &&
    dynamicPriceOptions?.values?.[0].type !== ServiceOptionType.CUSTOM
  );
}
